<script setup>
import { computed, useSlots, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useIsDarkTheme } from 'dashboard/composables/useIsDarkTheme';
import { useStore } from 'vuex';
import { resolveInboxThemeColor } from 'dashboard/helper/inboxColor';
import { formatNumber } from '@chatwoot/utils';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import ConversationBasicFilter from './widgets/conversation/ConversationBasicFilter.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';

const props = defineProps({
  pageTitle: { type: String, required: true },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  activeStatus: { type: String, required: true },
  isOnExpandedLayout: { type: Boolean, required: true },
  conversationStats: { type: Object, required: true },
  isListLoading: { type: Boolean, required: true },
  searchQuery: { type: String, default: '' },
  showSearch: { type: Boolean, default: true },
  inboxId: { type: [String, Number], default: null },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'basicFilterChange',
  'filtersModal',
  'update:searchQuery',
]);

const store = useStore();
const slots = useSlots();
const { t } = useI18n();
const isDarkTheme = useIsDarkTheme();

const inbox = computed(() => {
  if (!props.inboxId) return null;
  return store.getters['inboxes/getInbox'](props.inboxId) || null;
});

const inboxColor = computed(() => {
  if (!inbox.value) return null;
  void isDarkTheme.value;
  return resolveInboxThemeColor(inbox.value);
});

const hasChannelSurface = computed(() => Boolean(inboxColor.value && props.inboxId));

const inboxAvatarUrl = computed(() => inbox.value?.avatar_url || '');

const inboxAgents = computed(() => {
  if (!props.inboxId) return [];
  return store.getters['inboxAssignableAgents/getAssignableAgents'](Number(props.inboxId)) || [];
});

const inboxAgentCount = computed(() => inboxAgents.value.length);

const inboxConnected = computed(() => !inbox.value?.reauthorization_required);

const inboxStatusLabel = computed(() => {
  if (!hasChannelSurface.value) return '';
  const status = inboxConnected.value
    ? t('CHAT_LIST.HEADER_CONNECTED')
    : t('CHAT_LIST.HEADER_DISCONNECTED');
  if (!inboxAgentCount.value) return status;
  return `${status} · ${t('CHAT_LIST.HEADER_AGENTS', inboxAgentCount.value)}`;
});

watch(
  () => props.inboxId,
  id => {
    if (!id) return;
    store.dispatch('inboxAssignableAgents/fetch', [Number(id)]).catch(() => {});
  },
  { immediate: true }
);

const onBasicFilterChange = (value, type) => {
  emit('basicFilterChange', value, type);
};

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

const allCount = computed(() => props.conversationStats?.allCount || 0);
const formattedAllCount = computed(() => formatNumber(allCount.value));

const showComposeButton = computed(() => Boolean(Number(props.inboxId)));

const onSearchInput = event => {
  emit('update:searchQuery', event.target.value);
};

const onComposeOpen = toggleFn => {
  toggleFn();
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
};

const onComposeClose = () => {
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
};
</script>

<template>
  <div
    class="chat-list-header flex flex-col px-3 pt-3"
    :class="
      hasChannelSurface
        ? 'chat-list-header--channel mx-2 mt-2 mb-1 rounded-2xl border border-n-weak bg-n-alpha-3 dark:bg-n-solid-2 shadow-sm'
        : 'gap-3 pb-2'
    "
    :style="hasChannelSurface && inboxColor ? { '--inbox-accent': inboxColor } : undefined"
  >
    <div class="flex items-center justify-between gap-2 min-h-[2rem]">
      <div class="flex items-center min-w-0 gap-2.5 overflow-hidden">
        <div
          v-if="hasChannelSurface"
          class="size-8 rounded-full shrink-0 overflow-hidden bg-n-slate-3 grid place-items-center"
        >
          <img
            v-if="inboxAvatarUrl"
            :src="inboxAvatarUrl"
            :alt="pageTitle"
            class="size-full object-cover"
          />
          <span v-else class="i-lucide-message-circle size-4 text-n-slate-11" />
        </div>
        <div class="min-w-0">
          <h1
            class="text-base font-semibold truncate max-w-full leading-tight text-n-slate-12"
            :title="pageTitle"
          >
            {{ pageTitle }}
          </h1>
          <p
            v-if="inboxStatusLabel"
            class="mt-0.5 flex items-center gap-1.5 text-[11px] leading-none text-n-slate-11"
          >
            <span
              class="size-1.5 rounded-full shrink-0"
              :class="inboxConnected ? 'bg-n-teal-9' : 'bg-n-ruby-9'"
            />
            <span class="truncate">{{ inboxStatusLabel }}</span>
          </p>
        </div>
        <span
          v-if="
            allCount > 0 && hasAppliedFiltersOrActiveFolders && !isListLoading
          "
          class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize text-xxs shrink-0 bg-n-slate-3 text-n-slate-12"
          :title="allCount"
        >
          {{ formattedAllCount }}
        </span>
        <span
          v-if="
            !hasAppliedFiltersOrActiveFolders &&
            !searchQuery &&
            activeStatus &&
            activeStatus !== 'all' &&
            !hasChannelSurface
          "
          class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize text-xxs shrink-0 bg-n-slate-3 text-n-slate-12"
        >
          {{ $t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${activeStatus}.TEXT`) }}
        </span>
      </div>

      <ComposeConversation
        v-if="showComposeButton"
        class="flex-shrink-0"
        align-position="right"
        :prefill-inbox-id="inboxId"
        @close="onComposeClose"
      >
        <template #trigger="{ toggle, isOpen }">
          <NextButton
            v-tooltip.top="$t('COMPOSE_NEW_CONVERSATION.OPEN_TITLE')"
            icon="i-lucide-pen-line"
            color="slate"
            size="sm"
            class="!size-7"
            :class="{ '!bg-n-alpha-2': isOpen }"
            @click="onComposeOpen(toggle)"
          />
        </template>
      </ComposeConversation>
    </div>

    <div class="flex items-center gap-1.5 w-full">
      <div
        v-if="showSearch"
        class="conversation-search-box relative flex flex-1 items-center min-w-0 rounded-xl border border-n-weak bg-n-background" style="height: 1.65rem;"
      >
        <span
          class="pointer-events-none absolute ltr:left-2.5 rtl:right-2.5 top-1/2 -translate-y-1/2 i-lucide-search size-3.5 text-n-slate-10"
        />
        <input
          :value="searchQuery"
          type="search"
          class="conversation-search-input w-full min-w-0 border-0 text-sm outline-none mb-0 bg-transparent h-6 ltr:ml-[21px] rtl:mr-[21px] text-n-slate-12 placeholder:text-n-slate-10"
          :class="{ 'has-clear': searchQuery }"
          :placeholder="$t('CHAT_LIST.SEARCH.PLACEHOLDER')"
          @input="onSearchInput"
        />
        <button
          v-if="searchQuery"
          type="button"
          class="absolute ltr:right-2 rtl:left-2 top-1/2 -translate-y-1/2 i-lucide-x size-3.5 text-n-slate-10 hover:text-n-slate-12"
          @click="emit('update:searchQuery', '')"
        />
      </div>

      <div class="flex items-center gap-1 flex-shrink-0">
        <template v-if="hasAppliedFilters && !hasActiveFolders">
          <div class="relative">
            <NextButton
              v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
              icon="i-lucide-save"
              slate
              xs
              faded
              @click="emit('addFolders')"
            />
            <div
              id="saveFilterTeleportTarget"
              class="absolute z-50 mt-2"
              :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            />
          </div>
          <NextButton
            v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
            icon="i-lucide-circle-x"
            ruby
            faded
            xs
            @click="emit('resetFilters')"
          />
        </template>
        <template v-else-if="hasActiveFolders">
          <div class="relative">
            <NextButton
              id="toggleConversationFilterButton"
              v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
              icon="i-lucide-pen-line"
              slate
              xs
              faded
              @click="emit('filtersModal')"
            />
            <div
              id="conversationFilterTeleportTarget"
              class="absolute z-50 mt-2"
              :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            />
          </div>
          <NextButton
            id="toggleConversationFilterButton"
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
            icon="i-lucide-trash-2"
            ruby
            xs
            faded
            @click="emit('deleteFolders')"
          />
        </template>
        <template v-else>
          <div class="relative">
            <NextButton
              id="toggleConversationFilterButton"
              v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')"
              icon="i-lucide-list-filter"
              slate
              xs
              faded
              @click="emit('filtersModal')"
            />
            <div
              id="conversationFilterTeleportTarget"
              class="absolute z-50 mt-2"
              :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            />
          </div>
          <ConversationBasicFilter
            :is-on-expanded-layout="isOnExpandedLayout"
            @change-filter="onBasicFilterChange"
          />
        </template>
      </div>
    </div>

    <div
      v-if="slots.tabs"
      class="chat-list-header__tabs px-0 mt-0"
      :class="hasChannelSurface ? '' : 'border-t border-n-strong -mx-3 px-1 pt-1'"
    >
      <slot
        name="tabs"
        :on-channel-surface="hasChannelSurface"
        :accent-color="inboxColor"
      />
    </div>
  </div>
</template>

<style scoped>
.chat-list-header--channel {
  border-left: 5px solid var(--inbox-accent) !important;
  border-top: 2px solid var(--inbox-accent) !important;
  padding-bottom: 0.3rem;
  gap: 0.4rem;
}

.conversation-search-box {
  height: 1.65rem;
}

.conversation-search-input {
  outline-style: none !important;
  outline-width: 0 !important;
  margin-bottom: 0 !important;
  background: none !important;
  height: 1.65rem !important;
  margin-left: 21px !important;
  padding-top: 0;
  padding-bottom: 0;
  padding-left: 0;
  box-shadow: none !important;
  appearance: none;
  -webkit-appearance: none;
}

.conversation-search-input.has-clear {
  padding-right: 1.75rem;
}

.conversation-search-input::-webkit-search-decoration,
.conversation-search-input::-webkit-search-cancel-button,
.conversation-search-input::-webkit-search-results-button,
.conversation-search-input::-webkit-search-results-decoration {
  display: none;
}

.conversation-search-input:focus {
  outline: none;
  box-shadow: none;
  border: 0;
}
</style>
